import React from "react";
import { Search, X, Filter } from "lucide-react";
import { EFFECT_CATEGORIES } from "@/lib/sideEffectsCatalog";
import { SEVERITY_META, TREND_META } from "./constants";

export default function AllEffectsModal({
  effects,
  filteredEffects,
  allSearch, setAllSearch,
  allCategory, setAllCategory,
  allSortBy, setAllSortBy,
  onClose,
}) {
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="all-effects-modal">
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-4xl border border-stone-200 overflow-hidden flex flex-col" style={{ maxHeight: "90vh" }}>
        <div className="px-6 py-4 border-b border-stone-100 flex justify-between items-start">
          <div>
            <h3 className="font-display font-bold text-2xl text-stone-900">All Reported Effects</h3>
            <p className="text-sm text-stone-500 mt-0.5">{filteredEffects.length} of {effects.length} effects shown</p>
          </div>
          <button onClick={onClose} data-testid="close-all-effects" className="p-1.5 rounded-lg hover:bg-stone-100"><X className="w-5 h-5 text-stone-500" /></button>
        </div>

        <div className="px-6 py-4 border-b border-stone-100 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              data-testid="all-effects-search"
              value={allSearch}
              onChange={(e) => setAllSearch(e.target.value)}
              placeholder="Search effects…"
              className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-stone-500" />
            <span className="text-xs font-semibold text-stone-600 mr-1">Categories</span>
            {EFFECT_CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setAllCategory(c.id)}
                data-testid={`all-effects-cat-${c.id}`}
                className={`text-xs px-2.5 py-1 rounded-full border ${allCategory === c.id ? "bg-brand-500 text-white border-brand-700" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"}`}
              >
                {c.icon} {c.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-stone-600">Sort by:</span>
            <select data-testid="all-effects-sort" value={allSortBy} onChange={(e) => setAllSortBy(e.target.value)} className="text-xs px-2 py-1 rounded-md border border-stone-300 focus:border-brand-500 focus:outline-none">
              <option value="recent">Most Recent</option>
              <option value="severity">Severity</option>
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {filteredEffects.length === 0 && <div className="text-center text-stone-500 py-12">No effects match those filters.</div>}
          {filteredEffects.map((e) => {
            const sev = e.severity ? SEVERITY_META[e.severity] : null;
            const trd = e.trend ? TREND_META[e.trend] : null;
            return (
              <div key={e.id} className="border border-stone-200 rounded-xl p-4 hover:border-brand-300 transition-colors" data-testid={`all-effects-card-${e.id}`}>
                <div className="flex items-start gap-3">
                  {e.icon && <div className="text-3xl shrink-0" aria-hidden="true">{e.icon}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-display font-bold text-stone-900">{e.effect_name || "Free-text entry"}</h4>
                      {sev && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${sev.cls}`}><sev.Icon className="w-3 h-3" />{sev.label}</span>}
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
                      <div><div className="text-[11px] uppercase tracking-wider text-stone-500">Date</div><div className="text-stone-800">{new Date(e.created_at).toLocaleDateString()}</div></div>
                      <div><div className="text-[11px] uppercase tracking-wider text-stone-500">Frequency</div><div className="text-stone-800">{e.frequency || "—"}</div></div>
                      <div><div className="text-[11px] uppercase tracking-wider text-stone-500">Trend</div><div className="text-stone-800">{trd ? <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${trd.cls}`}><trd.Icon className="w-3 h-3" />{trd.label}</span> : "—"}</div></div>
                    </div>
                    {e.note && <div className="mt-3 px-3 py-2 rounded-lg bg-stone-50 border border-stone-100 text-sm text-stone-700 italic whitespace-pre-wrap">📝 {e.note}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
